import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { API, uploadFile, getAuthHeaders } from '../utils/api';
import { toast } from 'sonner';
import { Upload, Music, Image as ImageIcon, X } from 'lucide-react';

export default function SubmitPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submissionType, setSubmissionType] = useState('artist');
  const [loading, setLoading] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalType, setPaymentModalType] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    bio_text: '',
    instagram: '',
    twitter: '',
    youtube: '',
    spotify: ''
  });
  
  const [bioFile, setBioFile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [musicSamples, setMusicSamples] = useState([]);
  const [uploadingBio, setUploadingBio] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingMusic, setUploadingMusic] = useState(false);

  // Open payment in new window and show confirmation modal
  const openPaymentModal = (type, url) => {
    setPaymentModalType(type);
    // Open payment page in new window/tab
    window.open(url, '_blank', 'noopener,noreferrer');
    // Show confirmation modal
    setPaymentModalOpen(true);
  };

  // Close payment confirmation modal
  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setPaymentModalType('');
  };

  // Confirm payment and close modal
  const confirmPaymentDone = () => {
    setPaymentComplete(true);
    closePaymentModal();
    toast.success('Payment confirmation received! Complete your application below.');
  };

  // Check for payment session on page load
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sid = urlParams.get('session_id');
    if (sid) {
      setSessionId(sid);
      checkPaymentStatus(sid);
    }
  }, []);

  const checkPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/check-payment-status/${sid}`, {
        headers: getAuthHeaders()
      });

      if (response.data.payment_status === 'paid') {
        setPaymentComplete(true);
        toast.success('Payment verified! You can now submit your application.');
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
      } else {
        // Continue polling
        setTimeout(() => checkPaymentStatus(sid, attempts + 1), 2000);
      }
    } catch (error) {
      
      toast.error('Failed to verify payment');
    } finally {
      setCheckingPayment(false);
    }
  };

  const handlePayment = async () => {
    if (!user) {
      toast.error('Please login to submit');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const origin = window.location.origin;
      const response = await axios.post(
        `${API}/create-submission-payment`,
        { origin_url: origin },
        { headers: getAuthHeaders() }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Payment initiation failed');
      
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!user) {
      toast.error('Please login to upload files');
      navigate('/login');
      return;
    }
    
    setUploadingBio(true);
    try {
      const result = await uploadFile(file);
      setBioFile(result);
      toast.success('Bio uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload bio');
      
    } finally {
      setUploadingBio(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    if (!user) {
      toast.error('Please login to upload files');
      navigate('/login');
      return;
    }
    
    setUploadingPhotos(true);
    try {
      const uploads = await Promise.all(files.map(file => uploadFile(file)));
      setPhotos([...photos, ...uploads]);
      toast.success(`${uploads.length} photo(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload photos');
      
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleMusicUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    if (!user) {
      toast.error('Please login to upload files');
      navigate('/login');
      return;
    }
    
    setUploadingMusic(true);
    try {
      const uploads = await Promise.all(files.map(file => uploadFile(file)));
      setMusicSamples([...musicSamples, ...uploads]);
      toast.success(`${uploads.length} sample(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload music samples');
      
    } finally {
      setUploadingMusic(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to submit');
      navigate('/login');
      return;
    }

    if (!paymentComplete) {
      toast.error('Please confirm payment completion first');
      return;
    }
    
    setLoading(true);
    try {
      const submission = {
        submission_type: submissionType,
        full_name: formData.full_name,
        email: formData.email,
        bio_text: formData.bio_text,
        social_media: {
          instagram: formData.instagram,
          twitter: formData.twitter,
          youtube: formData.youtube,
          spotify: formData.spotify
        },
        bio_file_id: bioFile?.id || null,
        photos: photos.map(p => p.id),
        music_samples: musicSamples.map(m => m.id)
      };
      
      // Use CashApp payment method - no session_id required
      await axios.post(`${API}/submissions?payment_method=cashapp`, submission, {
        headers: getAuthHeaders()
      });
      
      toast.success('Submission received! We\'ll review it soon.');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed. Please try again.');
      
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-32 bg-[#1A1412]" data-testid="submit-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#FDFBF7] mb-4 tracking-tight">
            Submit Your Work
          </h1>
          <p className="text-[#A89B95] text-lg font-['Outfit']">
            Join the Vintage Ave Records family. Share your talent with us.
          </p>
        </div>

        <Card className="bg-[#2C221F] border-[#3D302C]">
          <CardHeader>
            <Tabs value={submissionType} onValueChange={setSubmissionType}>
              <TabsList className="grid w-full grid-cols-2 bg-[#1A1412]">
                <TabsTrigger 
                  value="artist" 
                  className="data-[state=active]:bg-[#D97736] data-[state=active]:text-white"
                  data-testid="artist-tab"
                >
                  Artist Submission
                </TabsTrigger>
                <TabsTrigger 
                  value="model" 
                  className="data-[state=active]:bg-[#D97736] data-[state=active]:text-white"
                  data-testid="model-tab"
                >
                  Model Submission
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {/* Payment Confirmation Modal */}
            {paymentModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" data-testid="payment-modal-overlay">
                <div className="relative w-full max-w-md mx-4 bg-[#2C221F] rounded-2xl border border-[#D97736]/30 overflow-hidden" data-testid="payment-modal">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-4 border-b border-[#3D302C]">
                    <h3 className="text-[#FDFBF7] font-['Playfair_Display'] text-xl font-bold">
                      Complete Payment
                    </h3>
                    <button
                      onClick={closePaymentModal}
                      className="p-2 text-[#A89B95] hover:text-[#FDFBF7] hover:bg-[#3D302C] rounded-full transition-colors"
                      data-testid="close-payment-modal"
                    >
                      <X size={24} />
                    </button>
                  </div>
                  
                  {/* Modal Content */}
                  <div className="p-6 text-center">
                    <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                      paymentModalType === 'CashApp' ? 'bg-[#00D632]' : 'bg-[#6366F1]'
                    }`}>
                      <span className="text-white text-3xl font-bold">
                        {paymentModalType === 'CashApp' ? '$' : 'JIM'}
                      </span>
                    </div>
                    
                    <h4 className="text-[#FDFBF7] text-lg font-semibold mb-2">
                      {paymentModalType} Payment Window Opened
                    </h4>
                    
                    <p className="text-[#A89B95] text-sm mb-6">
                      A new window has opened for you to complete your <strong className="text-[#CFB53B]">$25.00</strong> payment via {paymentModalType}. 
                      Please complete your payment in that window, then return here to confirm.
                    </p>
                    
                    <div className="bg-[#1A1412] p-4 rounded-lg mb-6 text-left">
                      <p className="text-[#CFB53B] text-sm font-semibold mb-2">Payment Instructions:</p>
                      <ol className="text-[#A89B95] text-sm space-y-2 list-decimal list-inside">
                        <li>Complete your $25.00 payment in the new window</li>
                        <li>Include &ldquo;Submission Fee&rdquo; in the payment note</li>
                        <li>Return here and click the button below</li>
                      </ol>
                    </div>
                    
                    <div className="space-y-3">
                      <Button
                        onClick={confirmPaymentDone}
                        className="w-full bg-[#00D632] hover:bg-[#00B82B] text-white rounded-full py-4 text-lg font-semibold"
                        data-testid="confirm-payment-done"
                      >
                        I&apos;ve Completed My Payment
                      </Button>
                      
                      <Button
                        onClick={closePaymentModal}
                        variant="ghost"
                        className="w-full text-[#A89B95] hover:text-[#FDFBF7] hover:bg-[#3D302C] rounded-full py-3"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Section */}
            {!paymentComplete && (
              <div className="mb-8 p-6 bg-[#1A1412] rounded-lg border-2 border-[#D97736]/30" data-testid="payment-section">
                <h3 className="text-[#FDFBF7] text-xl font-['Playfair_Display'] font-bold mb-4">
                  Step 1: Processing Fee
                </h3>
                <p className="text-[#A89B95] mb-4">
                  A $25 processing fee is required to submit your application. This helps us review and process all submissions professionally.
                </p>
                <div className="bg-[#2C221F] p-4 rounded-lg mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-[#FDFBF7]">Submission Processing Fee</span>
                    <span className="text-[#CFB53B] text-2xl font-bold">$25.00</span>
                  </div>
                </div>
                
                {/* Payment Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* CashApp Payment */}
                  <div className="bg-[#2C221F] p-6 rounded-lg text-center">
                    <div className="w-14 h-14 bg-[#00D632] rounded-xl flex items-center justify-center mx-auto mb-3">
                      <span className="text-white text-2xl font-bold">$</span>
                    </div>
                    <p className="text-[#FDFBF7] font-semibold mb-2">CashApp:</p>
                    <p className="text-[#00D632] text-xl font-bold mb-3">$vintageavemusic</p>
                    <button
                      onClick={() => openPaymentModal('CashApp', 'https://cash.app/$vintageavemusic/25')}
                      className="inline-block px-4 py-2 bg-[#00D632] hover:bg-[#00B82B] text-white rounded-full text-sm font-semibold transition-colors"
                      data-testid="cashapp-payment-btn"
                    >
                      Pay $25.00 via CashApp
                    </button>
                  </div>
                  
                  {/* JIM Payment */}
                  <div className="bg-[#2C221F] p-6 rounded-lg text-center">
                    <div className="w-14 h-14 bg-[#6366F1] rounded-xl flex items-center justify-center mx-auto mb-3">
                      <span className="text-white text-lg font-bold">JIM</span>
                    </div>
                    <p className="text-[#FDFBF7] font-semibold mb-2">Pay with JIM:</p>
                    <p className="text-[#6366F1] text-sm mb-3">Secure Payment</p>
                    <button
                      onClick={() => openPaymentModal('JIM', 'https://pay.jim.com/vintage-ave-records-llc/VC0x-6ntyKDZGV9-25.00')}
                      className="inline-block px-4 py-2 bg-[#6366F1] hover:bg-[#5355E8] text-white rounded-full text-sm font-semibold transition-colors"
                      data-testid="jim-payment-btn"
                    >
                      Pay $25.00 via JIM
                    </button>
                  </div>
                </div>
                
                <p className="text-[#A89B95] text-sm text-center mb-4">
                  Click a payment option above, complete your payment, then click below to continue with your application.
                </p>
                
                <Button
                  onClick={() => setPaymentComplete(true)}
                  className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full py-6 text-lg"
                  data-testid="payment-button"
                >
                  Verification in Process - Continue
                </Button>
              </div>
            )}

            {paymentComplete && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg" data-testid="payment-success">
                <p className="text-green-400 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  Payment will be verified! Complete your application below.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="full_name" className="text-[#FDFBF7]">Full Name</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736] focus:ring-[#D97736]/20"
                    data-testid="full-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#FDFBF7]">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736] focus:ring-[#D97736]/20"
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Bio */}
              <div>
                <Label htmlFor="bio_text" className="text-[#FDFBF7]">Bio / About You</Label>
                <Textarea
                  id="bio_text"
                  name="bio_text"
                  value={formData.bio_text}
                  onChange={handleInputChange}
                  rows={4}
                  className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736] focus:ring-[#D97736]/20"
                  placeholder="Tell us about your journey, style, and what makes you unique..."
                  data-testid="bio-textarea"
                />
              </div>

              {/* Bio File Upload */}
              <div>
                <Label className="text-[#FDFBF7]">Upload Bio Document (Optional)</Label>
                <div className="mt-2 border-2 border-dashed border-[#D97736]/30 rounded-lg p-6 text-center hover:border-[#D97736]/50 transition-colors">
                  <Upload className="mx-auto h-12 w-12 text-[#D97736] mb-2" />
                  <label htmlFor="bio-upload" className="cursor-pointer">
                    <span className="text-[#FDFBF7] hover:text-[#D97736] transition-colors" data-testid="bio-upload-label">
                      {uploadingBio ? 'Uploading...' : bioFile ? bioFile.original_filename : 'Click to upload PDF/DOC'}
                    </span>
                    <input
                      id="bio-upload"
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx"
                      onChange={handleBioUpload}
                      disabled={uploadingBio}
                      data-testid="bio-upload-input"
                    />
                  </label>
                </div>
              </div>

              {/* Photo Upload */}
              <div>
                <Label className="text-[#FDFBF7]">Upload Photos</Label>
                <div className="mt-2 border-2 border-dashed border-[#D97736]/30 rounded-lg p-6 text-center hover:border-[#D97736]/50 transition-colors">
                  <ImageIcon className="mx-auto h-12 w-12 text-[#D97736] mb-2" />
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    <span className="text-[#FDFBF7] hover:text-[#D97736] transition-colors" data-testid="photo-upload-label">
                      {uploadingPhotos ? 'Uploading...' : photos.length ? `${photos.length} photo(s) uploaded` : 'Click to upload photos'}
                    </span>
                    <input
                      id="photo-upload"
                      type="file"
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhotos}
                      data-testid="photo-upload-input"
                    />
                  </label>
                </div>
              </div>

              {/* Music Samples (Artist Only) */}
              {submissionType === 'artist' && (
                <div>
                  <Label className="text-[#FDFBF7]">Upload Music Samples</Label>
                  <div className="mt-2 border-2 border-dashed border-[#D97736]/30 rounded-lg p-6 text-center hover:border-[#D97736]/50 transition-colors">
                    <Music className="mx-auto h-12 w-12 text-[#D97736] mb-2" />
                    <label htmlFor="music-upload" className="cursor-pointer">
                      <span className="text-[#FDFBF7] hover:text-[#D97736] transition-colors" data-testid="music-upload-label">
                        {uploadingMusic ? 'Uploading...' : musicSamples.length ? `${musicSamples.length} sample(s) uploaded` : 'Click to upload MP3/WAV'}
                      </span>
                      <input
                        id="music-upload"
                        type="file"
                        className="hidden"
                        accept="audio/*"
                        multiple
                        onChange={handleMusicUpload}
                        disabled={uploadingMusic}
                        data-testid="music-upload-input"
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Social Media */}
              <div>
                <Label className="text-[#FDFBF7] mb-3 block">Social Media Links (Optional)</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    placeholder="Instagram URL"
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    data-testid="instagram-input"
                  />
                  <Input
                    name="twitter"
                    value={formData.twitter}
                    onChange={handleInputChange}
                    placeholder="Twitter/X URL"
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    data-testid="twitter-input"
                  />
                  <Input
                    name="youtube"
                    value={formData.youtube}
                    onChange={handleInputChange}
                    placeholder="YouTube URL"
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    data-testid="youtube-input"
                  />
                  <Input
                    name="spotify"
                    value={formData.spotify}
                    onChange={handleInputChange}
                    placeholder="Spotify URL"
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    data-testid="spotify-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading || uploadingBio || uploadingPhotos || uploadingMusic || !paymentComplete}
                className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {loading ? 'Submitting...' : paymentComplete ? 'Submit Application' : 'Confirm Payment Above First'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}